import {Color} from '@benchling/nucleus-ui';
import {Loader, Popover, ScrollArea, TextInput, Tooltip, UnstyledButton, createStyles} from '@mantine/core';
import {useDebouncedValue} from '@mantine/hooks';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {type FilterTreeNode, searchTree, visibleTreeRows} from 'omnibox/omniboxFilterTree';
import {pillSx, usePillStyles} from 'omnibox/omniboxPillStyles';
import type {OmniboxFilter} from 'omnibox/useTypeaheadData';
import BenchlingIcon, {type BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

// Share of the filter row's width the dropdown spans, centered beneath the omnibox so nested names
// have room after per-level indentation.
const DROPDOWN_WIDTH_RATIO = 0.8;
// Used until the filter row has been measured (e.g. when no anchor is provided).
const DROPDOWN_FALLBACK_WIDTH = 320;
// Per-level indentation in the tree, wide enough that each level clearly nests under its parent.
const INDENT_PX = 30;
// Height of the dropdown's scrolling list.
const LIST_HEIGHT = 420;
// Rows rendered at once; a broad search on a large tenant could otherwise match thousands of options.
const MAX_RENDERED_ROWS = 500;

const useStyles = createStyles((theme) => ({
  dropdown: {
    padding: 0,
    borderRadius: theme.radius.mdplus,
    overflow: 'hidden',
  },
  searchWrapper: {
    padding: `${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.sm}`,
  },
  // Fixed height so the dropdown holds a steady shape across states; scrolls on overflow.
  scrollArea: {
    height: LIST_HEIGHT,
  },
  optionList: {
    padding: `${theme.spacing.xs} ${theme.spacing.sm} ${theme.spacing.sm}`,
  },
  // Category label above a group of top-level options.
  sectionHeader: {
    padding: `${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.xs}`,
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.4,
    color: Color.GRAY_600,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    minHeight: 40,
    padding: '6px 8px',
    borderRadius: theme.radius.smplus,
    fontSize: theme.fontSizes.lg,
    color: Color.GRAY_900,
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
    '&[aria-selected="true"]': {
      backgroundColor: theme.colors.blue[0],
    },
  },
  optionName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  // A search result's location in the tree, right-aligned after its name.
  path: {
    flexShrink: 0,
    maxWidth: '50%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: theme.fontSizes.sm,
    color: Color.GRAY_600,
  },
  // Rounded tile holding an option's icon; a selected option shows a check in its place.
  iconTile: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    borderRadius: theme.radius.smplus,
    backgroundColor: Color.GRAY_100,
    color: Color.GRAY_600,
    fontSize: theme.fontSizes.md,
  },
  check: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    color: Color.GREEN_500,
    fontSize: theme.fontSizes.xl,
  },
  // A tree row pairs an expand/collapse toggle with the selectable option; indentation is applied
  // per-row by nesting depth.
  treeRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  treeSelect: {
    flex: 1,
    minWidth: 0,
    width: 'auto',
  },
  expandToggle: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: theme.radius.sm,
    color: Color.GRAY_600,
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },
  // Aligns rows with no children with those that have an expand toggle.
  expandSpacer: {
    flexShrink: 0,
    width: 22,
  },
  treeChevron: {
    fontSize: 12,
    color: Color.GRAY_600,
  },
  // Fill and center within the fixed-height list so the dropdown holds a steady shape across states.
  fullListState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: LIST_HEIGHT - 12,
    padding: '8px 12px',
    fontSize: 12,
    color: Color.GRAY_600,
    textAlign: 'center' as const,
  },
  truncationNote: {
    padding: '8px 12px',
    fontSize: 12,
    color: Color.GRAY_600,
    textAlign: 'center' as const,
  },
}));

// Mantine's `width` prop converts pixels to rem against a 16px base, which renders narrower under the
// app's smaller root font size; a class keeps the measured width in pixels. !important is needed to
// override the inline `width: max-content` Mantine sets when no `width` prop is given.
//
// The dropdown is anchored to the pill and shifted sideways by `marginLeft` to center it under the
// filter row. The shift is a margin rather than a floating-ui offset because the popover keeps applying
// the offset it opened with: its auto-update is not resubscribed when the offset changes, so a
// resize that changes the dropdown's width would leave it off center.
const useDropdownLayoutStyles = createStyles((_theme, {width, shift}: {width: number; shift: number}) => ({
  dropdown: {
    width: `${width}px !important`,
    marginLeft: shift,
  },
}));

/** Leading slot on an option row: its icon in a tile, or a check once selected. */
function OptionIcon({node, selected}: {node: FilterTreeNode; selected: boolean}): JSX.Element {
  const {classes} = useStyles();
  return selected ? (
    <span className={classes.check} role="img" aria-label="Selected">
      <BenchlingIcon icon="check" aria-hidden="true" />
    </span>
  ) : (
    <span
      className={classes.iconTile}
      style={node.iconColors && {backgroundColor: node.iconColors.bgColor, color: node.iconColors.color}}
    >
      <BenchlingIcon icon={node.icon} aria-hidden="true" />
    </span>
  );
}

/** Option label that shows the full name in a tooltip on hover, since long names are cut off. */
function OptionName({name}: {name: string}): JSX.Element {
  const {classes} = useStyles();
  return (
    // The omnibox Modal is zIndex 1055 and the filter dropdown 1056; the tooltip must sit above both.
    <Tooltip label={name} withinPortal={true} zIndex={1057} openDelay={300}>
      <span className={classes.optionName}>{name}</span>
    </Tooltip>
  );
}

// Identifies a selection. A schema listed under several options is told apart by the option it was
// picked from, so only that row shows as selected.
function filterKey(filter: OmniboxFilter): string {
  return `${filter.kind}:${filter.id}:${filter.optionKey ?? ''}`;
}

export interface OmniboxTreeFilterPillProps {
  label: string;
  icon: BenchlingIconName;
  isActive: boolean;
  // The filter row the dropdown is sized and centered against, rather than the pill itself.
  anchorRef: React.RefObject<HTMLDivElement>;
  // Options in display order; null while they load.
  nodes: ReadonlyArray<FilterTreeNode> | null;
  selectedFilters: ReadonlyArray<OmniboxFilter>;
  onSelect: (filter: OmniboxFilter) => void;
  searchPlaceholder: string;
  searchLabel: string;
  listLabel: string;
  loadingLabel: string;
  emptyMessage: string;
}

/**
 * A filter pill that opens a large, centered dropdown of tree-shaped options. Browsing shows the
 * tree, starting collapsed; a search flattens its matches into one level, each labeled with where
 * it sits in the tree. Searching and expanding are client-side.
 */
export default function OmniboxTreeFilterPill({
  label,
  icon,
  isActive,
  anchorRef,
  nodes,
  selectedFilters,
  onSelect,
  searchPlaceholder,
  searchLabel,
  listLabel,
  loadingLabel,
  emptyMessage,
}: OmniboxTreeFilterPillProps): JSX.Element {
  const {classes, cx} = useStyles();
  const {classes: pillClasses} = usePillStyles();
  const [opened, setOpened] = useState(false);
  const pillRef = useRef<HTMLButtonElement>(null);
  const [dropdownLayout, setDropdownLayout] = useState({width: DROPDOWN_FALLBACK_WIDTH, shift: 0});
  const {classes: layoutClasses} = useDropdownLayoutStyles(dropdownLayout);

  // The dropdown is anchored to the pill, so center it under the filter row by shifting it by the
  // distance between the pill's left edge and the centered dropdown's.
  const measureDropdown = useCallback((): void => {
    const anchor = anchorRef.current;
    const pill = pillRef.current;
    if (!anchor || !pill) {
      return;
    }
    const anchorRect = anchor.getBoundingClientRect();
    // An unmeasured row would collapse the dropdown; keep the last layout instead.
    if (anchorRect.width === 0) {
      return;
    }
    const width = Math.round(anchorRect.width * DROPDOWN_WIDTH_RATIO);
    const left = anchorRect.left + (anchorRect.width - width) / 2;
    setDropdownLayout({width, shift: Math.round(left - pill.getBoundingClientRect().left)});
  }, [anchorRef]);

  // The omnibox width is viewport-relative, so re-measure on resize while the dropdown is open.
  useEffect(() => {
    if (!opened) {
      return undefined;
    }
    window.addEventListener('resize', measureDropdown);
    return () => {
      window.removeEventListener('resize', measureDropdown);
    };
  }, [opened, measureDropdown]);

  const selectedKeys = useMemo(() => new Set(selectedFilters.map((f) => filterKey(f))), [selectedFilters]);
  const isSelected = (filter: OmniboxFilter): boolean => selectedKeys.has(filterKey(filter));

  const [search, setSearch] = useState('');
  const [debouncedSearchValue] = useDebouncedValue(search, 200);
  const searchQuery = debouncedSearchValue.trim();
  const searching = searchQuery !== '';
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(new Set());

  const toggleExpand = (id: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const treeRows = useMemo(
    () => (searching ? [] : visibleTreeRows(nodes ?? [], expandedIds)),
    [searching, nodes, expandedIds]
  );
  const searchResults = useMemo(
    () => (searching ? searchTree(nodes ?? [], searchQuery) : []),
    [searching, nodes, searchQuery]
  );
  const rowCount = searching ? searchResults.length : treeRows.filter((row) => row.type === 'item').length;

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      offset={14}
      withinPortal={true}
      // The omnibox Modal renders at zIndex 1055; the portaled dropdown must sit above it
      // (matching OmniboxFooter's Popover) or it renders behind the modal and is invisible.
      zIndex={1056}
      shadow="md"
      radius="md"
    >
      <Popover.Target>
        <UnstyledButton
          ref={pillRef}
          aria-pressed={isActive}
          aria-haspopup="listbox"
          aria-expanded={opened}
          onClick={() => {
            if (!opened) {
              measureDropdown();
            }
            setOpened((o) => !o);
          }}
          sx={pillSx(isActive)}
        >
          <span className={pillClasses.iconWrapper}>
            <BenchlingIcon icon={icon} className={pillClasses.pillIcon} aria-hidden="true" />
          </span>
          {label}
          <BenchlingIcon icon="chevron-down" className={pillClasses.pillChevron} aria-hidden="true" />
        </UnstyledButton>
      </Popover.Target>
      {/* Fixed width; Mantine otherwise sizes the dropdown to its content, which changes between states. */}
      <Popover.Dropdown className={cx(classes.dropdown, layoutClasses.dropdown)}>
        <div className={classes.searchWrapper}>
          <TextInput
            size="sm"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
            }}
            autoFocus={true}
            aria-label={searchLabel}
          />
        </div>
        <ScrollArea type="auto" className={classes.scrollArea}>
          <div className={classes.optionList} role="listbox" aria-label={listLabel}>
            {nodes == null ? (
              <div className={classes.fullListState} role="status" aria-label={loadingLabel}>
                <Loader size="xs" aria-hidden="true" />
              </div>
            ) : rowCount === 0 ? (
              <div className={classes.fullListState}>{emptyMessage}</div>
            ) : (
              <>
                {searching
                  ? searchResults.slice(0, MAX_RENDERED_ROWS).map(({node, path}) => {
                      const selected = isSelected(node.filter);
                      return (
                        <UnstyledButton
                          key={node.id}
                          role="option"
                          aria-selected={selected}
                          className={classes.option}
                          onClick={() => {
                            onSelect(node.filter);
                          }}
                        >
                          <OptionIcon node={node} selected={selected} />
                          <OptionName name={node.name} />
                          {path && <span className={classes.path}>{path}</span>}
                        </UnstyledButton>
                      );
                    })
                  : treeRows.slice(0, MAX_RENDERED_ROWS).map((row) => {
                      if (row.type === 'header') {
                        return (
                          <div
                            key={`section:${row.section}`}
                            className={classes.sectionHeader}
                            role="presentation"
                          >
                            {row.section}
                          </div>
                        );
                      }
                      const {node, depth, hasChildren} = row;
                      const selected = isSelected(node.filter);
                      const expanded = expandedIds.has(node.id);
                      return (
                        <div
                          key={node.id}
                          className={classes.treeRow}
                          style={{paddingLeft: depth * INDENT_PX}}
                        >
                          {hasChildren ? (
                            <UnstyledButton
                              className={classes.expandToggle}
                              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.name}`}
                              aria-expanded={expanded}
                              onClick={() => {
                                toggleExpand(node.id);
                              }}
                            >
                              <BenchlingIcon
                                icon={expanded ? 'chevron-down' : 'chevron-right'}
                                className={classes.treeChevron}
                                aria-hidden="true"
                              />
                            </UnstyledButton>
                          ) : (
                            <span className={classes.expandSpacer} />
                          )}
                          <UnstyledButton
                            role="option"
                            aria-selected={selected}
                            className={cx(classes.option, classes.treeSelect)}
                            onClick={() => {
                              onSelect(node.filter);
                            }}
                          >
                            <OptionIcon node={node} selected={selected} />
                            <OptionName name={node.name} />
                          </UnstyledButton>
                        </div>
                      );
                    })}
                {rowCount > MAX_RENDERED_ROWS && (
                  <div className={classes.truncationNote}>
                    Showing the first {MAX_RENDERED_ROWS}. Type to narrow the list.
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </Popover.Dropdown>
    </Popover>
  );
}
